import { useEffect, useMemo, useState } from 'react';
import {
  APPTOOLS_DOC,
  appToolMetaSchema,
  noteDocName,
  personalAppToolsDocName,
  sketchDocName,
  type AppToolMeta,
} from '@awwyeah/shared';
import type * as Y from 'yjs';
import { getDocManager, useDocLocalLoaded, useYDoc, useYMap } from '../../sync';
import { useUser } from '../auth';
import { useMyDocAccess } from '../sharing';
import {
  mirrorAppToolMeta,
  moveAppToolMeta,
  readAppToolMetaMirror,
  registryForAudience,
} from './appToolOps';

/**
 * The two Apps registries, merged (WS-15's registry-move pattern, mirrored from
 * `apps/web/src/modules/lists/useListRegistries.ts`). See that file's doc comment for the
 * full rationale — the short version: a title can't live in a doc everybody can open, so
 * private/member-shared tools' metadata lives in `apptools:user:<id>` instead, and a
 * "shared with me" tool's name comes from the meta mirrored into its own content doc,
 * because I can't open somebody else's personal registry to read it there.
 */

export type AppToolRegistry = 'family' | 'personal';
export type AppToolRegistrySource = AppToolRegistry | 'shared-with-me';

export interface RegisteredAppTool {
  meta: AppToolMeta;
  registry: AppToolRegistrySource;
  personal: boolean;
  sharedWithMe?: boolean;
}

export interface AppToolRegistries {
  tools: RegisteredAppTool[];
  metas: AppToolMeta[];
  familyMap: Y.Map<AppToolMeta>;
  personalMap: Y.Map<AppToolMeta> | null;
  placeInRegistry: (meta: AppToolMeta, audience: 'family' | 'private' | 'members') => void;
  registryOf: (toolId: string) => AppToolRegistrySource | null;
  mirror: (meta: AppToolMeta) => void;
  /**
   * True once both registries have finished their local (IndexedDB) load. `tools` is
   * legitimately empty before this — offline-first means the family/personal registries
   * load asynchronously from IndexedDB, and right after an update-triggered reload a
   * consumer that trusts an empty `tools` immediately would flash "no notes or sketches
   * yet" over a household that has plenty (found in the WS-update-reload audit).
   */
  locallyLoaded: boolean;
}

function contentDocName(meta: Pick<AppToolMeta, 'id' | 'kind'>): string {
  return meta.kind === 'note' ? noteDocName(meta.id) : sketchDocName(meta.id);
}

export function useAppToolRegistries(): AppToolRegistries {
  const { user } = useUser();

  const familyDoc = useYDoc(APPTOOLS_DOC);
  // Hooks can't be conditional — with no signed-in member we open `apptools` twice (same
  // doc, same socket, per the DocManager's name-keyed cache); `hasPersonal` stops it being
  // counted twice.
  const personalDocName = user ? personalAppToolsDocName(user.id) : APPTOOLS_DOC;
  const personalDoc = useYDoc(personalDocName);
  const hasPersonal = Boolean(user);

  const { map: familyMap, entries: familyEntries } = useYMap<AppToolMeta>(familyDoc, 'tools');
  const { map: personalMapRaw, entries: personalEntries } = useYMap<AppToolMeta>(
    personalDoc,
    'tools',
  );
  const personalMap = hasPersonal ? personalMapRaw : null;

  // Hooks can't be conditional, same reasoning as the `useYDoc` pair above: always call
  // `useDocLocalLoaded` for both doc names, and ignore the personal one when there's no
  // signed-in member to have a personal registry.
  const familyLocallyLoaded = useDocLocalLoaded(APPTOOLS_DOC);
  const personalLocallyLoaded = useDocLocalLoaded(personalDocName);
  const locallyLoaded = familyLocallyLoaded && (!hasPersonal || personalLocallyLoaded);

  const sharedWithMe = useSharedWithMeAppTools(user?.id ?? null);

  const tools = useMemo(() => {
    const byId = new Map<string, RegisteredAppTool>();
    for (const [, meta] of familyEntries) {
      byId.set(meta.id, { meta, registry: 'family', personal: false });
    }
    for (const meta of sharedWithMe) {
      byId.set(meta.id, { meta, registry: 'shared-with-me', personal: false, sharedWithMe: true });
    }
    if (hasPersonal) {
      // Personal last: it wins a duplicate, which is the narrower (safer) reading.
      for (const [, meta] of personalEntries) {
        byId.set(meta.id, { meta, registry: 'personal', personal: true });
      }
    }
    return [...byId.values()];
  }, [familyEntries, personalEntries, sharedWithMe, hasPersonal]);

  const metas = useMemo(() => tools.map((entry) => entry.meta), [tools]);

  const registryOf = (toolId: string): AppToolRegistrySource | null =>
    tools.find((entry) => entry.meta.id === toolId)?.registry ?? null;

  const mirror = (meta: AppToolMeta): void => {
    const handle = getDocManager().acquire(contentDocName(meta));
    mirrorAppToolMeta(handle.doc, meta);
    // The manager's 15s idle grace covers the flush; releasing immediately only stops the
    // socket being held open for a doc nothing is rendering.
    handle.release();
  };

  const placeInRegistry = (meta: AppToolMeta, audience: 'family' | 'private' | 'members'): void => {
    if (!personalMap) return;
    const target = registryForAudience(audience);
    const from = target === 'family' ? personalMap : familyMap;
    const to = target === 'family' ? familyMap : personalMap;
    mirror(meta); // the members it's shared with read the name from here
    if (!from.has(meta.id) && to.has(meta.id)) return; // already in the right place
    moveAppToolMeta(from, to, meta, user?.id);
  };

  return {
    tools,
    metas,
    familyMap,
    personalMap,
    placeInRegistry,
    registryOf,
    mirror,
    locallyLoaded,
  };
}

/**
 * NotePads/SketchPads other members have shared with me, by name — enumeration from
 * `GET /api/sharing` (rows where I'm a listed member), name from the meta mirrored inside
 * each content doc. Imperative rather than a per-doc React "bridge" component, same
 * reasoning as `useSharedWithMeLists`: the doc set is dynamic, hook counts are not.
 */
function useSharedWithMeAppTools(userId: string | null): AppToolMeta[] {
  const { byDocName } = useMyDocAccess();
  const [metas, setMetas] = useState<AppToolMeta[]>([]);

  const docNames = useMemo(
    () =>
      userId
        ? [...byDocName.values()]
            .filter((row) => row.ownerId !== userId && row.memberIds.includes(userId))
            .map((row) => row.docName)
            .filter((name) => name.startsWith('note:') || name.startsWith('sketch:'))
            .sort()
        : [],
    [byDocName, userId],
  );
  const key = docNames.join('|');

  useEffect(() => {
    if (docNames.length === 0) {
      setMetas([]);
      return undefined;
    }
    const handles = docNames.map((name) => getDocManager().acquire(name));
    const read = (): void => {
      setMetas(
        handles.flatMap((handle) => {
          const parsed = appToolMetaSchema.safeParse(readAppToolMetaMirror(handle.doc));
          return parsed.success ? [parsed.data] : [];
        }),
      );
    };
    const unsubscribes = handles.map((handle) => {
      const map = handle.doc.getMap('meta');
      const onChange = (): void => read();
      map.observe(onChange);
      return () => map.unobserve(onChange);
    });
    read();
    return () => {
      for (const unsubscribe of unsubscribes) unsubscribe();
      for (const handle of handles) handle.release();
    };
    // Keyed on the joined doc names, not the array — see `useSharedWithMeLists` for why.
  }, [key, docNames]);

  return metas;
}

/**
 * One tool's metadata, from whichever registry holds it, plus the `Y.Map` to write it back
 * to (mirrors `useListMeta`).
 */
export function useAppToolMeta(toolId: string | undefined): {
  meta: AppToolMeta | null;
  map: Y.Map<AppToolMeta> | null;
} {
  const registries = useAppToolRegistries();
  if (!toolId) return { meta: null, map: null };
  const entry = registries.tools.find((candidate) => candidate.meta.id === toolId) ?? null;
  if (!entry) return { meta: null, map: null };
  // Shared *with* me: no registry entry of mine. Writing to the family map would
  // republish the title to the whole household, so `null` is the honest answer.
  if (entry.sharedWithMe) return { meta: entry.meta, map: null };
  return {
    meta: entry.meta,
    map: entry.personal ? registries.personalMap : registries.familyMap,
  };
}
